import type { SmallGroupMessage } from "@/types";

/**
 * What the group screen can paint before the server answers: the newest page
 * of each recently-opened group, and enough of its header to name it.
 *
 * ## The page cache
 *
 * The newest page of each recently-opened group, so reopening one paints
 * immediately instead of after two round trips.
 *
 * The sibling of `chat-message-cache.ts`, and it exists because the group
 * thread never got the treatment the 1:1 thread did. Opening a group starts
 * from `messages = []` and then `load()` awaits `groupApi.get()` **and then**
 * `groupApi.messages()` — strictly sequential — so the reader watches a blank
 * thread for the length of two requests. Reported again on build 12 after the
 * inverted-list work fixed the wrong-position flash but not this.
 *
 * **In memory only, deliberately**, and the reasoning is inherited verbatim
 * from the 1:1 cache: persisting it would put plaintext group messages on the
 * device where the app has never put them, outliving the session and surviving
 * "Clear messages" unless every path swept it. That is a product and privacy
 * decision, not an implementation detail — so this dies with the process and
 * the trade is stated rather than taken quietly.
 *
 * Bounded on both axes per the repository rule. `Map` preserves insertion
 * order and writing re-inserts, so eviction drops the least recently written
 * group.
 */

/** Roughly a session's worth of groups; the rest cost one fetch, as before. */
export const MAX_GROUPS = 20;

/**
 * One page, matching `CATCH_UP_PAGE`. Holding more would not help: the screen
 * reads a page on open and walks backwards on demand, so a deeper cache would
 * go stale without ever being the thing that renders first.
 */
export const MAX_MESSAGES_PER_GROUP = 50;

const cache = new Map<string, SmallGroupMessage[]>();

/**
 * ## The header snapshot
 *
 * Enough to draw the two lines at the top of the group screen, and nothing
 * else. Reported on build 13: opening a group flashed "Small Group" /
 * "Temporary group nearby" and then swapped to the real name, because the
 * screen started from `group = null` and every path that already knew the
 * group — the inbox row, the home list, the create and join responses —
 * threw that knowledge away and refetched it.
 *
 * **A projection, deliberately, and not a `SmallGroup`.** The screen gates
 * real behaviour on that object: whether the composer is writable, whether
 * the leave option shows, whether this viewer is still a member. Seeding
 * those from memory would arm every one of them on stale data for the length
 * of a request. This type cannot: there is nothing in it to gate on.
 * `groupApi.get` remains the only authority.
 */
export type GroupHeaderSnapshot = {
  name: string;
  memberCount: number;
  maxMembers: number;
  /**
   * The expiry INSTANT, never a countdown.
   *
   * `SmallGroup.minutesLeft` is computed server-side at fetch time, so storing
   * it would freeze a number that describes a moment already past — the same
   * defect class as holding a coordinate in state and letting its `ageMs` stop
   * running (`docs/group-create-latency-plan.md` §2). An absolute instant
   * cannot go stale; the minutes are derived at render by `minutesLeftUntil`.
   */
  expiresAt: string;
};

const headers = new Map<string, GroupHeaderSnapshot>();

/** Null when nothing is known, so the caller shows nothing, not a guess. */
export function cachedGroupHeader(
  groupId: string | null | undefined,
): GroupHeaderSnapshot | null {
  if (!groupId) return null;
  return headers.get(groupId) ?? null;
}

/**
 * Record what we know, from whoever just saw it — a list row, a create, a
 * join, or the group screen's own authoritative read.
 *
 * Bounded on the same axis as the page cache and evicted the same way.
 */
export function rememberGroupHeader(
  groupId: string | null | undefined,
  group: {
    name: string;
    memberCount: number;
    maxMembers: number;
    expiresAt: string;
  },
): void {
  if (!groupId) return;
  if (!group.expiresAt) return;
  headers.delete(groupId);
  headers.set(groupId, {
    name: group.name,
    memberCount: group.memberCount,
    maxMembers: group.maxMembers,
    expiresAt: group.expiresAt,
  });
  while (headers.size > MAX_GROUPS) {
    const oldest = headers.keys().next().value;
    if (oldest === undefined) break;
    headers.delete(oldest);
  }
}

/**
 * Whole minutes left, floored at zero, derived at the moment of asking.
 *
 * Never negative: an expired group reads as 0 rather than counting downwards
 * past the end, which is what the screen's own `> 0` comparisons assume.
 */
export function minutesLeftUntil(
  expiresAt: string,
  now: number = Date.now(),
): number {
  const end = Date.parse(expiresAt);
  if (Number.isNaN(end)) return 0;
  return Math.max(0, Math.ceil((end - now) / 60_000));
}

/**
 * Never null, so a caller can seed `useState` with it directly.
 *
 * An empty result means "nothing worth painting" and deliberately does not
 * distinguish "never opened" from "known to be empty" — `rememberGroupMessages`
 * refuses to store an empty page precisely so that distinction cannot be drawn
 * and then acted on. That matters here: `threadLoaded` gates the "say hello"
 * placeholder, and a cache that claimed a group was empty would put that
 * sentence in front of a thread that simply had not arrived.
 */
export function cachedGroupMessages(
  groupId: string | null | undefined,
): SmallGroupMessage[] {
  if (!groupId) return [];
  return cache.get(groupId) ?? [];
}

export function rememberGroupMessages(
  groupId: string | null | undefined,
  messages: SmallGroupMessage[],
): void {
  if (!groupId) return;
  // An empty page is never stored — see `cachedGroupMessages`.
  if (messages.length === 0) return;
  const page = messages.slice(-MAX_MESSAGES_PER_GROUP);
  // Re-insert so this group becomes the most recently written.
  cache.delete(groupId);
  cache.set(groupId, page);
  while (cache.size > MAX_GROUPS) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

/** Drop one group entirely — leaving, removal, or an ending that clears it. */
export function forgetGroup(groupId: string | null | undefined): void {
  if (!groupId) return;
  cache.delete(groupId);
  headers.delete(groupId);
}

/** Drop everything, for sign-out and account switches. */
export function clearGroupCache(): void {
  cache.clear();
  headers.clear();
}
